/*
  Products Toolbar V2 — filter dropdowns + sort.

    - .js-top-filter-dd : multi-select filter dropdown (button pill + checkbox
                          panel). Several values of one filter can be selected
                          (OR — same semantics as the sidebar checkboxes).
                          Checkbox changes are debounced, then applied by
                          building a URL from the pill's data-all-url (current
                          URL minus this filter) plus every checked value.
    - .js-top-sort      : native <select>; sets the `sort_by` query param.

  Changes are applied WITHOUT a full page reload:
    1. If the facet sidebar (<facet-filters>) is on the page, its AJAX pipeline
       is used (keeps the sidebar's own state in sync).
    2. Otherwise the toolbar runs its own Section Rendering API update: fetch
       the filtered section, swap #filter-results and the #ptb2-top-filters
       pills, update the URL and re-init pagination.
    3. If both fail, fall back to a full-reload navigation.

  The pills are re-rendered (server-side truth) after every update; listeners
  are delegated to `document` so the swapped-in pills keep working, and the
  open panel is re-opened after the swap so multiple boxes can be ticked in a
  row. The Grid/List layout toggle and the mobile filter drawer are still
  handled by products-toolbar.js.
*/
(function () {
  const APPLY_DEBOUNCE_MS = 450;
  let applyTimer = null;
  let openKey = null; // data-filter-key of the currently open panel

  /* ---------- Panel open/close ---------- */

  function setOpen(dd, open) {
    const btn = dd.querySelector('.ptb2__dd-btn');
    const panel = dd.querySelector('.ptb2__dd-panel');
    if (!btn || !panel) return;
    btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    panel.hidden = !open;
    if (open) openKey = dd.dataset.filterKey || null;
    else if (openKey === dd.dataset.filterKey) openKey = null;
  }

  function closeAll(except) {
    document.querySelectorAll('.js-top-filter-dd').forEach((dd) => {
      if (dd !== except) setOpen(dd, false);
    });
  }

  /* ---------- Pill label ---------- */

  function refreshLabel(dd) {
    const btn = dd.querySelector('.ptb2__dd-btn');
    const label = dd.querySelector('.ptb2__dd-label');
    const clear = dd.querySelector('.ptb2__dd-clear');
    if (!btn || !label) return;
    const checked = Array.from(dd.querySelectorAll('input[type="checkbox"]:checked'));
    const names = checked.map((cb) => cb.dataset.label || '').filter(Boolean);
    label.textContent = names.length ? names.join(', ') : (btn.dataset.allLabel || '');
    btn.title = label.textContent;
    btn.classList.toggle('is-active', names.length > 0);
    if (clear) clear.hidden = names.length === 0;
  }

  /* ---------- Applying (AJAX with fallback) ---------- */

  // The facet sidebar component, if present and AJAX-capable.
  function getFacetComponent() {
    const el = document.querySelector('facet-filters');
    if (
      el
      && typeof el.applyFilters === 'function'
      && el.querySelector('.js-close-drawer-mob') // applyFilters requires it
    ) {
      return el;
    }
    return null;
  }

  /**
   * Standalone AJAX update via the Section Rendering API (used when the facet
   * sidebar isn't on the page). Mirrors facet-filters.js's applyFilters.
   * @param {string} params - URL search params (no leading "?").
   * @param {boolean} [updateUrl=true] - Push the new URL to history.
   * @returns {Promise<boolean>} false if the update could not be performed.
   */
  async function ajaxApply(params, updateUrl = true) {
    const results = document.getElementById('filter-results');
    const sectionEl = results && results.closest('.shopify-section');
    if (!results || !sectionEl || !sectionEl.id) return false;
    const sectionId = sectionEl.id.replace('shopify-section-', '');

    const customPagination = document.querySelector('custom-pagination');
    if (customPagination) customPagination.dataset.pauseInfiniteScroll = 'true';
    results.classList.add('is-loading');

    try {
      const fetchUrl = `${window.location.pathname}?${params}${params ? '&' : ''}section_id=${sectionId}`;
      const response = await fetch(fetchUrl);
      if (!response.ok) return false;

      const tmpl = document.createElement('template');
      tmpl.innerHTML = await response.text();

      const newResults = tmpl.content.querySelector('#filter-results');
      if (!newResults) return false;

      // Preserve the results list's class (grid/list layout state).
      const currentUl = results.querySelector('ul');
      const currentClass = currentUl && currentUl.getAttribute('class');
      results.innerHTML = newResults.innerHTML;
      const newUl = results.querySelector('ul');
      if (newUl && currentClass) newUl.setAttribute('class', currentClass);

      // Refresh the pills (their URLs/counts are relative to the filter state).
      const currentPills = document.getElementById('ptb2-top-filters');
      const newPills = tmpl.content.getElementById('ptb2-top-filters');
      if (currentPills && newPills) currentPills.innerHTML = newPills.innerHTML;

      // Refresh the results count used by the catalog search.
      const currentNum = document.querySelector('[data-num-results]');
      const newNum = tmpl.content.querySelector('[data-num-results]');
      if (currentNum && newNum) {
        currentNum.setAttribute('data-num-results', newNum.getAttribute('data-num-results'));
      }

      if (updateUrl) {
        window.history.pushState(
          { searchParams: params },
          '',
          `${window.location.pathname}${params ? '?'.concat(params) : ''}`
        );
      }

      if (customPagination && customPagination.reload) customPagination.reload();

      // Same event the facet pipeline emits — search / bundle filter hook into it.
      document.dispatchEvent(new CustomEvent('on:facet-filters:updated', { bubbles: true }));
      return true;
    } catch (error) {
      console.warn('[Toolbar] AJAX filter update failed', error); // eslint-disable-line
      return false;
    } finally {
      results.classList.remove('is-loading');
      if (customPagination) customPagination.dataset.pauseInfiniteScroll = 'false';
    }
  }

  // Apply a facet/sort URL — sidebar AJAX, own AJAX, then full reload.
  async function navigate(url) {
    const params = new URL(url, window.location.origin).searchParams.toString();

    const facets = getFacetComponent();
    if (facets) {
      facets.applyFilters(params, null, true);
      return;
    }

    const ok = await ajaxApply(params);
    if (!ok) window.location.href = url;
  }

  // Build the URL for a pill from its checked boxes and apply it.
  function applyDropdown(dd) {
    const btn = dd.querySelector('.ptb2__dd-btn');
    if (!btn || !btn.dataset.allUrl) return;
    const url = new URL(btn.dataset.allUrl, window.location.origin);
    dd.querySelectorAll('input[type="checkbox"]:checked').forEach((cb) => {
      if (cb.dataset.param) url.searchParams.append(cb.dataset.param, cb.dataset.value);
    });
    url.searchParams.delete('page');
    navigate(url.pathname + url.search);
  }

  /* ---------- Event delegation ---------- */

  document.addEventListener('click', (evt) => {
    const btn = evt.target.closest && evt.target.closest('.ptb2__dd-btn');
    if (btn) {
      const dd = btn.closest('.js-top-filter-dd');
      const isOpen = btn.getAttribute('aria-expanded') === 'true';
      closeAll(dd);
      setOpen(dd, !isOpen);
      return;
    }

    const clear = evt.target.closest && evt.target.closest('.ptb2__dd-clear');
    if (clear) {
      const dd = clear.closest('.js-top-filter-dd');
      dd.querySelectorAll('input[type="checkbox"]').forEach((cb) => {
        cb.checked = false;
      });
      refreshLabel(dd);
      clearTimeout(applyTimer);
      applyDropdown(dd);
      return;
    }

    // Outside click closes every panel.
    if (!evt.target.closest || !evt.target.closest('.js-top-filter-dd')) closeAll();
  });

  document.addEventListener('change', (evt) => {
    const cb = evt.target.closest && evt.target.closest('.js-top-filter-dd input[type="checkbox"]');
    if (cb) {
      const dd = cb.closest('.js-top-filter-dd');
      refreshLabel(dd);
      clearTimeout(applyTimer);
      applyTimer = setTimeout(() => applyDropdown(dd), APPLY_DEBOUNCE_MS);
      return;
    }

    const sortSel = evt.target.closest && evt.target.closest('.js-top-sort');
    if (sortSel) {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', sortSel.value);
      url.searchParams.delete('page');
      navigate(url.toString());
    }
  });

  document.addEventListener('keydown', (evt) => {
    if (evt.key === 'Escape') closeAll();
  });

  // Back/forward within AJAX-applied filters (facet-filters handles its own).
  window.addEventListener('popstate', () => {
    if (getFacetComponent()) return;
    ajaxApply(window.location.search.replace(/^\?/, ''), false);
  });

  // The pills were re-rendered (server truth): restore the open panel so the
  // shopper can keep ticking boxes.
  document.addEventListener('on:facet-filters:updated', () => {
    if (!openKey) return;
    const dd = document.querySelector(
      `.js-top-filter-dd[data-filter-key="${window.CSS && CSS.escape ? CSS.escape(openKey) : openKey}"]`
    );
    if (dd) setOpen(dd, true);
  });

  // Browsers restore prior form state on back/forward navigation (incl.
  // bfcache) — re-sync every checkbox to the server-rendered state so a pill
  // can't claim a filter the page isn't actually applying.
  window.addEventListener('pageshow', () => {
    document.querySelectorAll('.js-top-filter-dd').forEach((dd) => {
      dd.querySelectorAll('input[type="checkbox"]').forEach((cb) => {
        cb.checked = cb.hasAttribute('checked');
      });
      refreshLabel(dd);
    });
  });
})();
